import {_} from "lodash";
import * as ko from "knockout";
import {global, lm, lm_} from "../appContext";
import {EhubCommunicator, EHubManagerOptions, LanguageManager} from "../../ehub/EHubManager";
import {getAnyValue, getWebServiceUrl} from "../util/misc";
import {EventListenerManager} from "../util/a";
import pubsub from "../pubsub";
import {KOSubscriptionTracker} from "../util/KOSubscriptionTracker";
import {getRouteDefinition} from "../constants/RouteDefinitions";
import {EHUB_IDS, ObjectTypeId} from "../constants";
import {IExpressionBuilderParams} from "../component/dynamic-variable/dialog/builder";
import {storeObservable} from "../util/sessionstore";
import {IRouteDefinitionParams} from "../index";
import {applyEHubStylesheetCacheBust, getEHubAssetUrl} from "./assetVersion";
import {withCacheBustTimestamp} from "../util/cacheBustQuery";


export class Manager {

    public readonly isReady: KnockoutObservable<boolean> = ko.observable(false);
    public readonly isStarted: KnockoutObservable<boolean> = ko.observable(false);
    public eHubManager!: EHubManager;
    private eventListenerManager = new EventListenerManager()
    private readonly kosub = new KOSubscriptionTracker()
    public fnResolve: (value: number | boolean) => void = () => {};
    public fnReject: (reason?: any) => void = () => {};
    private events: Partial<IEHubManagerEvents> = {};
    private element: HTMLElement | null = null;
    private timeoutBeforeDialog!: number;
    /** While init_js runs, concurrent callers (login preload + route navigation) share one promise. */
    private initPromise: Promise<void> | null = null;

    public init(): Promise<void> {
        if (this.eHubManager) {
            return Promise.resolve()
        }
        if (this.initPromise) {
            return this.initPromise
        }
        this.initPromise = this.loadEHubManager()
            .finally(() => {
                this.initPromise = null
            })
        return this.initPromise
    }

    /** Inject ehub/index.js if needed, subscribe to language changes, construct EHubManager. */
    private loadEHubManager(): Promise<void> {
        this.kosub.subscribe(lm.selectedLanguage, () => {
            if (this.eHubManager) {
                this.eHubManager.update(this.getEHubManagerOptions({}))
            }
        })
        return Promise
            .all([
                this.init_js(),
                applyEHubStylesheetCacheBust(),
            ])
            .then(() => {
                if (!window.EHubManager) {
                    throw new Error('ehub/index.js loaded but window.EHubManager is missing')
                }
                this.eHubManager = new window.EHubManager(EHUB_IDS.ehub_id)
                this.isReady(true)
            })
    }

    public async init_js(): Promise<boolean> {
        if (window.EHubManager) {
            return true
        }
        const existingScript = document.getElementById(EHUB_IDS.script_id) as HTMLScriptElement | null
        if (existingScript) {
            // Another init_js() may have inserted the tag; if load already fired, 'load' will never run again.
            if (this.isScriptElementLoaded(existingScript)) {
                return true
            }
            return new Promise((resolve, reject) => {
                existingScript.addEventListener('load', () => resolve(true), {once: true})
                existingScript.addEventListener('error', () => reject(new Error('ehub/index.js failed to load')), {once: true})
            })
        }

        let src = await getEHubAssetUrl('index.js')
        



        return new Promise((resolve, reject) => {
            const el_script = document.createElement('script')
            el_script.addEventListener('load', () => resolve(true), {once: true})
            el_script.addEventListener('error', () => reject(new Error('ehub/index.js failed to load')), {once: true})
            el_script.id = EHUB_IDS.script_id
            el_script.type = 'text/javascript'
            el_script.src = src
            document.documentElement.append(el_script)
        })
    }

    /** Whether a script tag finished loading before we could subscribe to its load event. */
    private isScriptElementLoaded(script: HTMLScriptElement): boolean {
        if (window.EHubManager) {
            return true
        }
        const extended = script as HTMLScriptElement & { complete?: boolean; readyState?: string }
        return extended.complete === true
            || extended.readyState === 'complete'
            || extended.readyState === 'loaded'
    }

    private getEHubManagerOptions_base(): Pick<EHubManagerOptions, 'token' | 'orgnr' | 'apiUrl' | 'languageId' | 'languageManager'> {
        return {
            token: global.login.getToken(),
            orgnr: global.login.selectedOrganizationId() ?? '',
            apiUrl: getWebServiceUrl('' as any).replace(/\/+$/, ''),
            languageId: lm.selectedLanguage().id,
            languageManager: lm_ as unknown as LanguageManager,
        }
    }

    private getEHubManagerOptions(options: Partial<EHubManagerOptions>): EHubManagerOptions {
        return _.defaults(options, this.getEHubManagerOptions_base())
    }

    public start(options: Partial<EHubManagerOptions>, events?: Partial<IEHubManagerEvents>): Promise<void> {
        if (this.isStarted()) {
            this.events = events ?? {}
            this.eHubManager.update(this.getEHubManagerOptions(options))
            return Promise.resolve()
        }

        this.element = document.getElementById(EHUB_IDS.ehub_id)
        if (!this.element) {
            throw new Error(`#${EHUB_IDS.ehub_id} not found`)
        }
        this.events = events ?? {}
        const element = this.element;

        this.eventListenerManager.add([element, 'close', () => this.stop(), false])

        this.eventListenerManager.add([element, 'delete', () => {
            global.cacheManager.removeCache('searchformtemplate')
            global.cacheManager.removeCache('getformtemplate')
            global.cacheManager.removeCache('searchobject')
            if (events?.delete) {
                this.eventListenerManager.add([element, 'delete', events.delete(), false])
            }
        }, false])

        this.eventListenerManager.add([element, 'cancel', () => {
            this.eHubManager.stop()
            this.isStarted(false)
            this.eventListenerManager.dispose()
            pubsub.trigger('search.needs.refresh')
            if (events?.cancel) {
                this.eventListenerManager.add([element, 'cancel', events.cancel(), false])
            }
        }, false])

        this.eventListenerManager.add([element, 'submit', () => {
            global.cacheManager.removeCache('searchobject')
            setTimeout(() => {
                if (events?.submit) {
                    this.eventListenerManager.add([element, 'submit', events.submit(), false])
                }
                this.stop()
            }, 5000);
        }, false])

        const mountOptions = this.getEHubManagerOptions(options)
        const communicatorParams = this.buildCommunicatorParams()
        // Ensure script/manager exist even when login did not preload (e.g. emulated open).
        return this.init()
            .then(() => this.waitForLoginBootstrapThenPaint())
            .then(() => {
                if (this.isStarted()) {
                    return
                }
                this.eHubManager.start(mountOptions)
                this.eHubManager.show()
                this.eHubManager.registerCommunicator(communicatorParams)
                this.isStarted(true)
            })
    }

    /** Defer Vue mount until login bootstrap finishes and Knockout layout has painted. */
    private waitForLoginBootstrapThenPaint(): Promise<void> {
        return new Promise((resolve) => {
            // Double rAF: first frame after KO bindings apply; second after layout/paint.
            const afterPaint = () => {
                requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
            }
            if (!global.login.isFinishingLoginBootstrap()) {
                afterPaint()
                return
            }
            const sub = global.login.isFinishingLoginBootstrap.subscribe((active) => {
                if (!active) {
                    sub.dispose()
                    afterPaint()
                }
            })
        })
    }

    private buildCommunicatorParams(): EhubCommunicator {
        const fnComplete = (dialogId: string, cb: () => void) => {
            global.dialogManager.remove(dialogId)
            global.stateCss.useEAdmStylesheet(false)
            this.eHubManager.show()
            cb()
        }

        const communicatorParams: EhubCommunicator = {}
        communicatorParams.createMessageTemplate = (
            value: number,
            callback: (value: number) => void,
            error: (error: any) => void
        ): Promise<number> => {
            return new Promise((resolve, reject) => {
                let dialogId: string

                this.fnResolve = (value: number | boolean) => {
                    fnComplete(dialogId, () => callback(value as number))
                }

                this.fnReject = (reason) => {
                    fnComplete(dialogId, () => error(reason))
                }

                this.eHubManager.hide()
                this.addDialog(() => {
                    const params: IRouteDefinitionParams = {
                        __routeId: 'createmessageflowehub',
                        __routeDef: _.defaultsDeep({showHeading: false}, getRouteDefinition('createmessageflowehub')),
                        __routeParams: {
                            formid: value,
                        },
                    }
                    dialogId = global.dialogManager.addComponent({
                        title: 'btn.create.collection',
                        content: {
                            name: 'component/messages/createehub',
                            params: params,
                        },
                        fullWidth: true,
                    });
                })
            })
        }
        communicatorParams.updateMessageTemplate = (
            value: number,
            callback: (value: boolean) => void,
            error: (error: any) => void
        ): Promise<boolean> => {
            return new Promise((resolve, reject) => {
                let dialogId: string

                this.fnResolve = (value: number | boolean) => {
                    fnComplete(dialogId, () => callback(!!value))
                }
                this.fnReject = (reason) => {
                    fnComplete(dialogId, () => error(reason))
                }

                this.eHubManager.hide()
                this.addDialog(() => {
                    const def = getRouteDefinition('editmessageflowehub');
                    if (!def) {
                        return;
                    }
                    const params: IRouteDefinitionParams = {
                        __routeId: def.id,
                        __routeDef: _.defaultsDeep({showHeading: false}, def),
                        __routeParams: {
                            id: value,
                        },
                    }
                    dialogId = global.dialogManager.addComponent({
                        title: getAnyValue(def.label) ?? '',
                        content: _.defaultsDeep({params}, def.component),
                        fullWidth: true,
                    });
                })
            })
        }
        communicatorParams.deleteMessageTemplate = (
            value: number,
            callback: (value: boolean) => void,
            error: (error: any) => void
        ): Promise<boolean> => {
            return new Promise((resolve, reject) => {
                let dialogId: string

                this.fnResolve = (value: number | boolean) => {
                    fnComplete(dialogId, () => callback(!!value))
                }
                this.fnReject = (reason) => {
                    fnComplete(dialogId, () => error(reason))
                }

                this.eHubManager.hide()
                this.addDialog(() => {
                    const def = getRouteDefinition('deletemessageflowehub');
                    if (!def) {
                        return;
                    }
                    const params: IRouteDefinitionParams = {
                        __routeId: def.id,
                        __routeDef: _.defaultsDeep({showHeading: false}, def),
                        __routeParams: {
                            id: value,
                        },
                    }
                    dialogId = global.dialogManager.addComponent({
                        title: getAnyValue(def.label) ?? '',
                        content: _.defaultsDeep({params}, def.component),
                        fullWidth: true,
                    });
                })
            })
        }

        if (storeObservable('eadm.expressionbuilder.ehub')()) {
            communicatorParams.editFunctionExpression = (
                expression: string
            ): Promise<string> => {
                return new Promise((resolve) => {
                    const params: IExpressionBuilderParams = {
                        objectTypeIds: [
                            ObjectTypeId.Function,
                            ObjectTypeId.History,
                            ObjectTypeId.Event,
                            ObjectTypeId.System,
                            ObjectTypeId.Message,
                            ObjectTypeId.Ruleset,
                            ObjectTypeId.FunctionAlias,
                        ],
                        expression: expression,
                        save: (expression) => resolve(expression),
                        // Cancel = no change (echo original). '' is only for explicit clear-on-save.
                        cancel: () => resolve(expression),
                    };

                    this.addDialog(() => {
                        global.dialogManager.addCustom({
                            name: 'component/dynamic-variable/dialog/builder',
                            params: params,
                        });
                    })
                })
            }
        }

        return communicatorParams
    }

    public stop() {
        clearTimeout(this.timeoutBeforeDialog)
        global.dialogManager.removeAll()
        this.eHubManager.stop()
        global.stateCss.useEAdmStylesheet(true)
        global.stateCss.useEHubStylesheet(false)
        this.isStarted(false)
        this.eventListenerManager.dispose()
        this.fnReject = () => {};
        this.fnResolve = () => {};
        global.cacheManager.removeCache('searchformtemplate')
        global.cacheManager.removeCache('getformtemplate')
        global.cacheManager.removeCache('searchobject')
        pubsub.trigger('search.needs.refresh')
        if (this.events?.close && this.element) {
            this.eventListenerManager.add([this.element, 'close', this.events.close(), false])
        }
    }

    public update(options: Pick<EHubManagerOptions, 'form' | 'mode'>) {
        this.eHubManager.update(this.getEHubManagerOptions(options))
    }

    private addDialog(fn: () => void) {
        // due to "stylesheet shuffling" and for bootstrap dialogs to work we'll have to delay displaying
        // the dialog for a bit
        global.stateCss.useEAdmStylesheet(true)
        window.clearTimeout(this.timeoutBeforeDialog)
        this.timeoutBeforeDialog = window.setTimeout(fn, 100)
    }

}